import { useTheme } from '@mui/material/styles';
import React from 'react';

import { Modal as MUIModal, ModalBody, ModalFooter, ModalHeader } from 'components/core/Modal';
import { Box, Button, IconButton, Tooltip, Typography } from 'ui';
import { Backdrop } from '../Backdrop';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material';
import InfoSVG from '../../assets/icons/info';

type ModalProps = {
  isOpen: boolean;
  children: React.ReactNode;
  title?: React.ReactNode;
  titleHint?: string;
  className?: string;
  hideCloseButton?: boolean;
  hideFooter?: boolean;
  onSuccess?: (...args: any[]) => void;
  onCancel: () => void;
  width?: number;
  successButtonTitle?: string;
  cancelButtonTitle?: string;
  formId?: string | null;
  disableSuccessButton?: boolean;
  isLegacy?: boolean;
  showOverlay?: boolean;
};

const BlockingOverlay = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.modal + 1
}));

export const Modal = ({
  isOpen,
  children,
  title,
  titleHint,
  className,
  successButtonTitle = 'Add',
  cancelButtonTitle = 'Cancel',
  hideCloseButton = true,
  hideFooter,
  onSuccess,
  onCancel,
  width = 1010,
  formId,
  disableSuccessButton,
  isLegacy = false,
  showOverlay = false
}: ModalProps): JSX.Element => {
  const theme = useTheme();
  return (
    <MUIModal className={className} isOpen={isOpen} width={width} onRequestHide={onCancel}>
      {showOverlay && <BlockingOverlay open={true} showSpinner={true} />}
      <ModalHeader
        sx={{
          background: theme.palette.grey[600],
          padding: '24px 24px 0 24px',
          flexDirection: 'row'
        }}
      >
        {title && (
          <Typography
            variant="h6"
            sx={{
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'start',
              color: theme.palette.text.primary
            }}
          >
            {title}
            {titleHint && (
              <Tooltip title={titleHint}>
                <div>
                  <Box sx={{ pl: 1, cursor: 'pointer' }}>
                    <InfoSVG />
                  </Box>
                </div>
              </Tooltip>
            )}
          </Typography>
        )}
        {!hideCloseButton ? (
          <IconButton
            aria-label="close"
            onClick={onCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </ModalHeader>
      <ModalBody
        sx={{
          background: theme.palette.grey[600],
          display: 'flex',
          flexDirection: 'row',
          padding: '0 24px 24px 24px',
          width: '100%'
        }}
        isLegacy={isLegacy}
      >
        {children}
      </ModalBody>
      {!hideFooter && (
        <ModalFooter sx={{ background: theme.palette.grey[600], padding: '0 24px 24px 24px' }} isLegacy={isLegacy}>
          <Button color="secondary" onClick={onCancel} sx={{ margin: '0 10px' }}>
            {cancelButtonTitle}
          </Button>
          <Button
            disabled={disableSuccessButton}
            type={formId ? 'submit' : undefined}
            form={formId || undefined}
            onClick={onSuccess}
            sx={{ minWidth: 80, height: 40 }}
          >
            {successButtonTitle}
          </Button>
        </ModalFooter>
      )}
    </MUIModal>
  );
};

export default Modal;
